import { useState, useCallback, useEffect } from "react";

const STORAGE_PREFIX = "filters";

function getStorageKey(pageKey: string): string {
  return `${STORAGE_PREFIX}/${pageKey}`;
}

/**
 * Hook pour un état persisté dans localStorage (filtres d'une page).
 * Au montage, lit la valeur depuis localStorage ; à chaque mise à jour, la sauvegarde.
 */
export function usePersistedState<T>(
  pageKey: string,
  initialValue: T,
  options?: {
    serialize?: (value: T) => string;
    deserialize?: (raw: string) => T | null;
  }
): [T, (value: T | ((prev: T) => T)) => void] {
  const storageKey = getStorageKey(pageKey);
  const { serialize, deserialize } = options ?? {};

  const [state, setState] = useState<T>(() => {
    if (typeof window === "undefined") return initialValue;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw == null) return initialValue;
      if (deserialize) {
        const parsed = deserialize(raw);
        return parsed ?? initialValue;
      }
      const parsed = JSON.parse(raw) as T;
      return parsed ?? initialValue;
    } catch {
      return initialValue;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const toStore = serialize ? serialize(state) : JSON.stringify(state);
      localStorage.setItem(storageKey, toStore);
    } catch {
      // ignore quota / private mode
    }
  }, [storageKey, state, serialize]);

  const setValue = useCallback((value: T | ((prev: T) => T)) => {
    setState((prev) => (typeof value === "function" ? (value as (prev: T) => T)(prev) : value));
  }, []);

  return [state, setValue];
}
